import {TimelineSectionSliceDefaultPrimarySectionsItem} from 'prismicio-types'
import {PrismicRichText} from '@prismicio/react'
import {PrismicNextImage} from '@prismicio/next'
import cn from 'classnames'
import {useRef} from 'react'
import {useInView, motion, useScroll, useTransform} from 'framer-motion'
import {isFilled} from '@prismicio/client'

const itemVariants = {
  hidden: {opacity: 0},
  visible: (custom: number) => ({
    opacity: 1,
    transition: {delay: custom * 0.2, duration: 0.6, ease: 'easeIn'},
  }),
}

type TimelineCardProps = {
  section: TimelineSectionSliceDefaultPrimarySectionsItem
  index: number
}

const ANIMATION_INTERVALS = [
  [0.2, 0.3],
  [0.3, 0.4],
]

export const TimelineCard = ({section, index}: TimelineCardProps) => {
  const ref = useRef(null)
  const isInView = useInView(ref, {amount: 0.1, once: false})

  const {scrollYProgress} = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  })

  // Animation for left separator (appears when index < 2)
  const leftWidth = useTransform(scrollYProgress, ANIMATION_INTERVALS[index] || [0, 1], ['0%', '100%'])

  return (
    <div className="flex flex-col gap-3 max-md:relative max-md:pl-[25px] md:gap-12" ref={ref}>
      {/* Mobile dot */}
      <div className="absolute left-0 top-3 h-[8px] w-[8px] rounded-[1px] bg-moss-grey-300 md:hidden" />
      {/* Mobile separator */}
      {index < 2 && (
        <div className="absolute left-[3px] top-[24px] h-[calc(100%+16px)] w-[1.5px] bg-moss-grey-150 md:hidden" />
      )}
      <div className="relative flex md:justify-center">
        <div className="bg-moss-white px-2">
          <motion.div
            className={cn(
              'text-button-s md:text-button flex h-8 w-fit items-center justify-center justify-self-center rounded-[4px] px-4 uppercase',
              index === 2 ? 'bg-moss-blue text-moss-white' : 'bg-moss-grey-100 text-black',
            )}
            custom={index}
            variants={itemVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            <span className="uppercase">{section.time_label}</span>
          </motion.div>
        </div>
        {/* Desktop separator */}
        {index < 2 && (
          <motion.div
            style={{width: leftWidth}}
            className="absolute left-[calc(50%_+_48px)] top-1/2 h-0.5 -translate-y-1/2 bg-moss-grey-150 max-md:hidden"
          />
        )}
      </div>
      <motion.div
        className={cn(
          'flex min-h-[317px] w-full flex-col justify-between rounded-lg border border-[#000]/[10%] bg-[#FBFBFB] md:max-lg:h-full md:max-lg:min-h-0',
          index === 2 ? 'bg-moss-blue-light' : 'bg-[#FBFBFB]',
        )}
        custom={index}
        variants={itemVariants}
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
      >
        <div className="px-6 pt-6 max-md:pb-6 md:max-lg:pb-6">
          <PrismicRichText
            field={section.content}
            components={{
              paragraph: ({children}) => (
                <p className="text-tg-body-l text-black max-md:leading-[normal]">{children}</p>
              ),
              list: ({children}) => (
                <ul className="text-tg-body-m flex flex-col gap-1 pt-3 font-normal text-black/[60%] md:pt-2">
                  {children}
                </ul>
              ),
              listItem: ({children}) => (
                <li className="flex items-center gap-2.5">
                  <div className="h-[5px] w-[5px] rounded-[1px] bg-[#C4C4C4]" />
                  <p>{children}</p>
                </li>
              ),
            }}
          />
        </div>
        <div className="md:max-lg:hidden">
          <picture>
            {isFilled.imageThumbnail(section.asset.Mobile) && (
              <source
                media="(max-width: 768px)"
                srcSet={section.asset.Mobile.url + '?auto=compress,format&fit=max&w=768'}
                sizes="50vw"
              />
            )}
            <PrismicNextImage field={section.asset} className="max-h-[171px] w-full object-contain" />
          </picture>
        </div>
      </motion.div>
    </div>
  )
}
